// The Evolve Labs product registry.
// Adding a product here surfaces it across the ecosystem, the constellation,
// and the products index — no component changes required.

export const STATUS = {
    LIVE: 'LIVE',
    BETA: 'BETA',
    BUILDING: 'BUILDING',
    ACTIVE_DEVELOPMENT: 'ACTIVE DEVELOPMENT',
    IN_DEVELOPMENT: 'IN DEVELOPMENT',
    EXPERIMENTAL: 'EXPERIMENTAL',
    RESEARCH: 'RESEARCH',
    COMING_SOON: 'COMING SOON',
};

export const products = [
    {
        id: 'codemetrics',
        name: 'CodeMetrics',
        category: 'Developer Tools',
        status: STATUS.LIVE,
        year: '2025',
        platform: 'Web',
        accent: '#3ECFCF',
        summary:
            'A hands-on software learning platform built around real coding environments, AI guidance, structured paths, and project-based development.',
        href: 'https://codemetricslab.com',
        external: true,
        domain: 'codemetricslab.com',
    },
    {
        id: 'evolved-ui',
        name: 'Evolved UI',
        category: 'Design Systems',
        status: STATUS.BETA,
        year: '2026',
        platform: 'Web',
        accent: '#FF7A45',
        summary:
            'The interface layer behind Evolve Labs products — a component library and design system built for consistency, speed, and motion.',
        href: '/ui/',
        external: false,
        // Same origin, but a separate static app rather than a route in this
        // one — needs a real navigation. Trailing slash skips the 301 hop.
        standalone: true,
        domain: 'ui',
    },
    {
        id: 'agl',
        name: 'Amateur Gaming League',
        short: 'AGL',
        category: 'Competitive Gaming',
        status: STATUS.LIVE,
        year: '2025',
        platform: 'Web',
        accent: '#F0B429',
        summary:
            'A competitive gaming platform for teams, leagues, seasons, standings, match reporting, and tournament competition.',
        href: 'https://theagleague.com',
        external: true,
        domain: 'theagleague.com',
    },
    {
        id: 'relayr',
        name: 'RelayR',
        category: 'Communication',
        status: STATUS.IN_DEVELOPMENT,
        year: '2025',
        platform: 'iOS / Web',
        accent: '#6C5CE7',
        summary:
            'A communication platform designed around real-time conversations and connected gaming communities.',
        href: '/relayr',
        external: false,
        domain: 'relayr',
    },
    {
        id: 'authentiq',
        name: 'Authentiq',
        category: 'AI',
        status: STATUS.RESEARCH,
        year: '2026',
        platform: 'API / Web',
        accent: '#E8E8ED',
        summary:
            'An AI authenticity engine focused on detecting manipulated and AI-generated digital content.',
        href: 'https://authentiqvision.com',
        external: true,
        domain: 'authentiqvision.com',
    },
    {
        id: 'liftlog',
        name: 'LiftLog',
        category: 'Fitness',
        status: STATUS.LIVE,
        year: '2024',
        platform: 'iOS',
        accent: '#D4A843',
        summary:
            'A modern workout tracking product designed around performance, progress, and simplicity.',
        href: '/liftlog',
        external: false,
        domain: 'liftlog',
    },
    {
        id: 'pulse',
        name: 'Evolve Pulse',
        category: 'Data Systems',
        status: STATUS.LIVE,
        year: '2025',
        platform: 'Web',
        accent: '#EF002F',
        summary:
            'Real-time insights and intelligent dashboards — the instrumentation layer behind how our products learn.',
        href: 'https://useevolvepulse.com',
        external: true,
        domain: 'useevolvepulse.com',
    },
    {
        id: 'portrait',
        name: 'Evolve Portrait',
        category: 'Imaging',
        status: STATUS.EXPERIMENTAL,
        year: '2026',
        platform: 'iOS',
        accent: '#5AC8FA',
        summary:
            'AI-assisted portrait enhancement and professional photo editing technology.',
        href: null,
        external: false,
        domain: null,
    },
];

// Areas of active exploration inside the lab.
export const researchAreas = [
    { name: 'Artificial Intelligence', note: 'Applied models, agents, inference' },
    { name: 'Machine Learning', note: 'Training pipelines, evaluation' },
    { name: 'Computer Vision', note: 'Detection, authenticity, imaging' },
    { name: 'Developer Tools', note: 'Environments, analysis, learning' },
    { name: 'Gaming Infrastructure', note: 'Leagues, matchmaking, standings' },
    { name: 'Communication Platforms', note: 'Real-time, presence, community' },
    { name: 'Automation', note: 'Workflows, orchestration' },
    { name: 'Mobile Applications', note: 'iOS, cross-platform' },
    { name: 'Data Systems', note: 'Telemetry, storage, analytics' },
];

// Work that has not become a product yet.
export const experiments = [
    { code: 'LAB / 001', title: 'On-device inference for imaging', field: 'Computer Vision', state: 'Prototyping' },
    // Moments — deliberately described at the systems layer, not the product layer.
    { code: 'LAB / 002', title: 'Ephemeral state capture with low-latency fan-out', field: 'Real-Time Systems', state: 'Prototyping' },
    // Vant — same treatment.
    { code: 'LAB / 003', title: 'Constraint-solving for dynamic supply allocation', field: 'Distributed Systems', state: 'Prototyping' },
    { code: 'LAB / 004', title: 'Synthetic media provenance signals', field: 'AI', state: 'Research' },
];

// The stack behind the lab.
export const stack = [
    { group: 'Product', items: ['Web Applications', 'Mobile Applications', 'Platforms'] },
    { group: 'Engineering', items: ['React', 'TypeScript', 'Node', 'Java', 'Python'] },
    { group: 'Intelligence', items: ['AI', 'Machine Learning', 'Computer Vision'] },
    { group: 'Data', items: ['PostgreSQL', 'MongoDB', 'Redis'] },
    { group: 'Infrastructure', items: ['AWS', 'Docker', 'CI/CD'] },
];
